import { AdgroupConfig, AdgroupFormData, CampaignFormData, FlowControlMode, SmartBidType } from '@tt4b/ttam-enums';
import { computed, Ref, ComputedRef } from '@vue/composition-api';
import { FlowControlModeOptionItem } from './types';
import { i18n } from './../../../i18n';
import { useStdAdgroupCbo, useStdAdgroupIs } from '@tt4b/ttam-hooks';

interface useCheckIfSupportOptions {
  adgroupData: Ref<AdgroupFormData>;
  campaignData: Ref<CampaignFormData>;
}

export function useCheckIfSupportAccelerate(props: useCheckIfSupportOptions) {
  const { adgroupData, campaignData } = props;
  const { isCBO } = useStdAdgroupCbo({
    adgroupFormData: adgroupData,
    campaignFormData: campaignData,
  });

  const supportAccelerate = computed(() => {
    // 如果开启了 CBO，只能匀速投放 （STANDARD）
    if (isCBO.value) {
      return false;
    }

    // MaxCV、no bid、Roas下只能选择匀速投放 （STANDARD）
    if (adgroupData.value?.smartBidType !== SmartBidType.STANDARD) {
      return false;
    }

    return true;
  });

  return {
    supportAccelerate,
  };
}

export function useCheckIfSupportStandard(props: useCheckIfSupportOptions) {
  const { adgroupData, campaignData } = props;
  const { isCLA } = useStdAdgroupIs({
    adgroupFormData: adgroupData,
    campaignFormData: campaignData,
  });

  const supportStandard = computed(() => {
    // 如果是CLA，不支持standard，只能加速投放，
    if (isCLA.value) {
      return false;
    }

    return true;
  });

  return {
    supportStandard,
  };
}

export function useOptionsPropsFlowControlModes(
  props: useCheckIfSupportOptions & {
    adgroupConfig: Ref<AdgroupConfig>;
  },
) {
  const { adgroupData, adgroupConfig, campaignData } = props;

  const { supportAccelerate } = useCheckIfSupportAccelerate({
    adgroupData,
    campaignData,
  });

  const { supportStandard } = useCheckIfSupportStandard({
    adgroupData,
    campaignData,
  });

  const flowControlModes: ComputedRef<FlowControlModeOptionItem[]> = computed(() => [
    {
      value: FlowControlMode.STANDARD,
      text: i18n.t('adgroup_pacing_standard'),
      tipsText: i18n.t('adgroup_pacing_standard_hint'),
      disabled: adgroupConfig.value.disableDeliveryType || !supportStandard.value,
      clickKey: 'create_adgroup_b_and_o_delivery_type',
      clickParams: {
        'data-tea-delivery-type': 'standerd',
      },
    },
    {
      value: FlowControlMode.ACCELERATE,
      text: i18n.t('adgroup_pacing_accelerated'),
      tipsText: i18n.t('adgroup_pacing_accelerated_hint'),
      disabled: adgroupConfig.value.disableDeliveryType || !supportAccelerate.value,
      clickKey: 'create_adgroup_b_and_o_delivery_type',
      clickParams: {
        'data-tea-delivery-type': 'accelerated',
      },
      tooltipHoverKey: 'adgroup_delivery_accelerated_hint_hover',
    },
  ]);
  return {
    options: flowControlModes,
  };
}

export function useCboInfoPropsVisible(props: {
  adgroupData: Ref<AdgroupFormData>;
  campaignData: Ref<CampaignFormData>;
}) {
  const { adgroupData, campaignData } = props;

  const { isCBO } = useStdAdgroupCbo({
    adgroupFormData: adgroupData,
    campaignFormData: campaignData,
  });
  return {
    visible: computed(() => isCBO.value),
  };
}
